import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { KENLO_PAYMENTS_OPENING_RETRY_DELAY_MS } from "../constants/kenloPayments";
import { DEFAULT_CHART_PERIOD } from "../constants/dashboardPeriods";
import { DASHBOARD_PAYMENTS_PAGE_SIZE } from "../constants/dashboardListPagination";
import { fetchKenloPayments } from "../services/ownerPatrimonyService";
import {
  DashboardPeriodPreset,
  IDashboardFilters,
  IKenloPaymentsData,
  IKenloPaymentsSummary,
  IOwnerKenloEndpoints,
  IPaginationMeta,
  IPayment,
  IVisitPeriod,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { KenloPaymentsQuery } from "../utils/buildKenloPaymentsParams";
import { getChartMonthsRange } from "../utils/getDashboardChartPeriodRange";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { getCurrentMonthPeriod } from "../utils/getCurrentMonthPeriod";
import { mergeKenloPaymentsForFilter } from "../utils/mergeKenloPaymentsForFilter";
import { resolveKenloPropertyFilterOptions } from "../utils/normalizeKenloPaymentsData";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import {
  kenloLoadAttemptLimit,
  resolveKenloRequestTimeoutMs,
} from "../utils/kenloPaymentsLoad";

interface UseKenloPaymentsOptions {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
}

interface UseKenloPaymentsResult {
  summary: IKenloPaymentsSummary | null;
  payments: IPayment[];
  filterPayments: IPayment[];
  propertyFilterOptions: KenloPropertyOption[];
  paymentsPagination: IPaginationMeta | null;
  filters: IDashboardFilters | null;
  query: KenloPaymentsQuery;
  status: OwnerPatrimonyRequestStatus;
  setChartPeriod: (period: DashboardPeriodPreset) => Promise<void>;
  setChartMonths: (months: number | "all") => Promise<void>;
  setPaymentsPeriod: (period: IVisitPeriod) => Promise<void>;
  setKenloPropertyId: (kenloPropertyId: string | null) => Promise<void>;
  setPaymentsPage: (page: number) => Promise<void>;
  retry: () => Promise<void>;
}

function waitForKenloRetry(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, KENLO_PAYMENTS_OPENING_RETRY_DELAY_MS);
  });
}

function createDefaultQuery(): KenloPaymentsQuery {
  return {
    chartPeriod: DEFAULT_CHART_PERIOD,
    chartZoom: 12,
    paymentsPeriod: getCurrentMonthPeriod(),
    kenloPropertyId: null,
    propertyId: null,
    paymentsPage: 1,
    paymentsPerPage: DASHBOARD_PAYMENTS_PAGE_SIZE,
  };
}

function buildKenloLoadKey(
  accessToken: string,
  endpoints?: IOwnerKenloEndpoints
): string {
  return `${accessToken}:${JSON.stringify(endpoints ?? {})}`;
}

export function useKenloPayments({
  accessToken,
  enabled,
  endpoints,
}: UseKenloPaymentsOptions): UseKenloPaymentsResult {
  const [data, setData] = useState<IKenloPaymentsData | null>(null);
  const [filterPayments, setFilterPayments] = useState<IPayment[]>([]);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [query, setQuery] = useState<KenloPaymentsQuery>(createDefaultQuery);
  const queryRef = useRef(query);
  const historyYearsRef = useRef<number | undefined>(undefined);
  const latestRequestIdRef = useRef(0);
  const initialLoadKeyRef = useRef<string | null>(null);
  const hasCompletedLoadRef = useRef(false);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const loadKenloPayments = useCallback(
    async (requestQueryInput: KenloPaymentsQuery) => {
      if (!enabled || !accessToken) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      setQuery(requestQueryInput);
      setStatus("loading");

      api.defaults.headers.authorization = `Bearer ${accessToken}`;
      const requestQuery =
        requestQueryInput.chartPeriod === "all_time"
          ? {
              ...requestQueryInput,
              historyYears: requestQueryInput.historyYears ?? historyYearsRef.current,
            }
          : requestQueryInput;
      const attemptLimit = kenloLoadAttemptLimit(hasCompletedLoadRef.current);

      for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
        try {
          const response = await fetchWithTimeout(
            fetchKenloPayments(requestQuery, endpoints),
            resolveKenloRequestTimeoutMs(hasCompletedLoadRef.current)
          );

          if (requestId !== latestRequestIdRef.current) {
            return;
          }

          historyYearsRef.current = response.summary?.history_years;
          setData(response);
          setFilterPayments((currentPayments) =>
            mergeKenloPaymentsForFilter(currentPayments, response.payments ?? [])
          );
          hasCompletedLoadRef.current = true;
          setStatus("success");
          return;
        } catch {
          if (requestId !== latestRequestIdRef.current) {
            return;
          }

          const hasAnotherAttempt = attempt < attemptLimit - 1;
          if (!hasAnotherAttempt) {
            setStatus("error");
            return;
          }

          await waitForKenloRetry();

          if (requestId !== latestRequestIdRef.current) {
            return;
          }
        }
      }
    },
    [accessToken, enabled, endpoints]
  );

  const setChartPeriod = useCallback(
    async (period: DashboardPeriodPreset) => {
      const chartZoom =
        period === "all_time"
          ? "all"
          : period === "last_12_months"
            ? 12
            : period === "last_6_months"
              ? 6
              : 12;

      await loadKenloPayments({
        ...queryRef.current,
        chartPeriod: period,
        chartCustomRange: undefined,
        chartZoom,
      });
    },
    [loadKenloPayments]
  );

  const setChartMonths = useCallback(
    async (months: number | "all") => {
      if (months === "all") {
        await setChartPeriod("all_time");
        return;
      }

      await loadKenloPayments({
        ...queryRef.current,
        chartPeriod: "last_12_months",
        chartCustomRange: getChartMonthsRange(months),
        chartZoom: months,
      });
    },
    [loadKenloPayments, setChartPeriod]
  );

  const setPaymentsPeriod = useCallback(
    async (paymentsPeriod: IVisitPeriod) => {
      await loadKenloPayments({
        ...queryRef.current,
        paymentsPeriod,
        paymentsPage: 1,
      });
    },
    [loadKenloPayments]
  );

  const setKenloPropertyId = useCallback(
    async (kenloPropertyId: string | null) => {
      await loadKenloPayments({
        ...queryRef.current,
        kenloPropertyId,
        propertyId: null,
        paymentsPage: 1,
      });
    },
    [loadKenloPayments]
  );

  const setPaymentsPage = useCallback(
    async (page: number) => {
      await loadKenloPayments({
        ...queryRef.current,
        paymentsPage: page,
      });
    },
    [loadKenloPayments]
  );

  const retry = useCallback(async () => {
    await loadKenloPayments(queryRef.current);
  }, [loadKenloPayments]);

  useEffect(() => {
    if (!enabled || !accessToken) {
      initialLoadKeyRef.current = null;
      setFilterPayments([]);
      return;
    }

    const loadKey = buildKenloLoadKey(accessToken, endpoints);

    if (initialLoadKeyRef.current === loadKey) {
      return;
    }

    initialLoadKeyRef.current = loadKey;
    void loadKenloPayments(createDefaultQuery());
  }, [accessToken, enabled, endpoints, loadKenloPayments]);

  return {
    summary: data?.summary ?? null,
    payments: data?.payments ?? [],
    filterPayments,
    propertyFilterOptions: resolveKenloPropertyFilterOptions(
      data?.property_filter_options,
      mergeKenloPaymentsForFilter(filterPayments, data?.payments ?? [])
    ),
    paymentsPagination: data?.payments_pagination ?? null,
    filters: data?.filters ?? null,
    query,
    status,
    setChartPeriod,
    setChartMonths,
    setPaymentsPeriod,
    setKenloPropertyId,
    setPaymentsPage,
    retry,
  };
}
